import { useState, useEffect, useMemo } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { productionApi } from '../../api/production.api';
import type { CropTask, CropTaskType } from '../../types/production.types';

const TYPE_LABELS: Record<string, string> = {
  SEEDING: 'Semis',
  TRANSPLANT: 'Repiquage',
  WATERING: 'Arrosage',
  WEEDING: 'Désherbage',
  HARVEST: 'Récolte',
  TREATMENT: 'Traitement',
  FERTILIZING: 'Fertilisation',
  PRUNING: 'Taille',
  MULCHING: 'Paillage',
  HILLING: 'Buttage',
  NETTING: 'Filet',
  STAKING: 'Tuteurage',
  THINNING: 'Éclaircissage',
  OTHER: 'Autre',
};

const TYPE_COLORS: Record<string, string> = {
  SEEDING: '#22c55e',
  TRANSPLANT: '#3b82f6',
  WATERING: '#06b6d4',
  WEEDING: '#f59e0b',
  HARVEST: '#ef4444',
  TREATMENT: '#8b5cf6',
  FERTILIZING: '#84cc16',
  PRUNING: '#f97316',
  MULCHING: '#a16207',
  HILLING: '#78716c',
  NETTING: '#0ea5e9',
  STAKING: '#14b8a6',
  THINNING: '#ec4899',
  OTHER: '#6b7280',
};

const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

function formatDayHeader(key: string): string {
  const d = new Date(`${key}T12:00:00`);
  const s = d.toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDuration(totalMin: number): string {
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
}

/**
 * Journal des interventions : toutes les tâches EFFECTUÉES, jour par jour,
 * comme un registre — la vue à présenter lors d'un contrôle (Certisys).
 */
export default function InterventionsJournal() {
  const { tenant, loading: tenantLoading } = useTenant();

  const [tasks, setTasks] = useState<CropTask[]>([]);
  const [workers, setWorkers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState<number | null>(null); // 0-11, null = tous
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    if (!tenantLoading && tenant) loadData();
  }, [tenant, tenantLoading]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [done, harvests, fieldWorkers] = await Promise.all([
        productionApi.listCropTasks({ completed: true }),
        productionApi.listHarvests().catch(() => []),
        productionApi.listFieldWorkers().catch(() => []),
      ]);

      // Les récoltes entrent dans le journal comme des interventions « Récolte »
      const harvestEntries: CropTask[] = (harvests as any[]).map((h) => {
        const qty = Number(h.quantity_kg);
        const isPieces = (h.notes || '').includes('PIÈCES');
        return {
          id: `harvest-${h.id}`,
          crop_id: h.crop_id,
          type: 'HARVEST' as CropTaskType,
          title: `Récolte — ${h.crop?.variety?.species || 'culture'} : ${qty} ${isPieces ? 'pièces/bottes' : 'kg'}`,
          description: (h.notes || '').replace('[recolte-pos] ', '') || null,
          planned_week: 0,
          planned_date: null,
          completed_at: h.harvest_date,
          completed_by: h.harvested_by || null,
          is_auto_generated: false,
          priority: 2,
          duration_minutes: null,
          actual_duration: null,
          photos: null,
          notes: null,
          created_at: h.created_at,
          crop: h.crop,
        };
      });

      setTasks([...done.filter((t) => t.completed_at), ...harvestEntries]);
      setWorkers(Object.fromEntries(fieldWorkers.map((w) => [w.id, w.name])));
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const years = useMemo(() => {
    const set = new Set(tasks.map((t) => new Date(t.completed_at!).getFullYear()));
    set.add(new Date().getFullYear());
    return Array.from(set).sort((a, b) => b - a);
  }, [tasks]);

  const typesPresent = useMemo(() => {
    return Array.from(new Set(tasks.map((t) => t.type))).sort();
  }, [tasks]);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      const d = new Date(t.completed_at!);
      if (d.getFullYear() !== yearFilter) return false;
      if (monthFilter !== null && d.getMonth() !== monthFilter) return false;
      if (typeFilter && t.type !== typeFilter) return false;
      return true;
    });
  }, [tasks, yearFilter, monthFilter, typeFilter]);

  // Regroupement par jour, ordre chronologique (comme un registre papier)
  const byDay = useMemo(() => {
    const map = new Map<string, CropTask[]>();
    for (const t of filtered) {
      const key = dayKey(t.completed_at!);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const totalMinutes = filtered.reduce((s, t) => s + (t.actual_duration || 0), 0);
  const activeDays = byDay.length;

  if (tenantLoading || loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Impression : ne garder que le journal */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #journal-print, #journal-print * { visibility: visible; }
          #journal-print { position: absolute; left: 0; top: 0; width: 100%; }
          #journal-print .no-print { display: none; }
        }
      `}</style>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Journal des interventions</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Registre chronologique des tâches effectuées — jour par jour
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm"
        >
          🖨 Imprimer
        </button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <select className="input w-28" value={yearFilter} onChange={(e) => setYearFilter(parseInt(e.target.value))}>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <button
          onClick={() => setMonthFilter(null)}
          className={`px-3 py-1.5 rounded-full text-sm ${monthFilter === null ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          Année
        </button>
        {MONTH_LABELS.map((label, i) => (
          <button
            key={label}
            onClick={() => setMonthFilter(monthFilter === i ? null : i)}
            className={`px-3 py-1.5 rounded-full text-sm ${monthFilter === i ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {label}
          </button>
        ))}
        <select className="input w-44 ml-auto" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">Tous les types</option>
          {typesPresent.map((t) => (
            <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
          ))}
        </select>
      </div>

      {/* Journal */}
      <div id="journal-print">
        <div className="mb-4">
          <p className="text-sm text-gray-500">
            <strong className="text-gray-800">Les Folies Maraîchères</strong> — registre des interventions culturales ·{' '}
            {yearFilter}
            {monthFilter !== null ? ` · ${MONTH_LABELS[monthFilter]}` : ''} ·{' '}
            {filtered.length} intervention{filtered.length > 1 ? 's' : ''} sur {activeDays} jour{activeDays > 1 ? 's' : ''}
            {totalMinutes > 0 && <> · {formatDuration(totalMinutes)} de travail enregistré</>}
          </p>
        </div>

        {byDay.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
            Aucune intervention sur cette période
          </div>
        ) : (
          <div className="space-y-4">
            {byDay.map(([key, dayTasks]) => (
              <div key={key} className="bg-white rounded-lg shadow overflow-hidden" style={{ breakInside: 'avoid' }}>
                <div className="px-4 py-2 bg-green-50 border-b border-green-100 flex items-baseline justify-between">
                  <h3 className="font-semibold text-sm text-green-900">{formatDayHeader(key)}</h3>
                  <span className="text-xs text-green-700">
                    {dayTasks.length} intervention{dayTasks.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="divide-y divide-gray-50">
                  {dayTasks.map((t) => (
                    <div key={t.id} className="px-4 py-2.5 flex items-start gap-3">
                      <span
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-white shrink-0 mt-0.5"
                        style={{ backgroundColor: TYPE_COLORS[t.type] || '#6b7280' }}
                      >
                        {TYPE_LABELS[t.type] || t.type}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{t.title}</p>
                        <p className="text-xs text-gray-500">
                          {t.crop?.variety?.species}
                          {t.crop?.plot?.code && <> — planche {t.crop.plot.code}</>}
                          {t.description && <span className="italic"> · {t.description}</span>}
                        </p>
                        {Array.isArray(t.photos) && t.photos.length > 0 && (
                          <div className="flex gap-1.5 mt-1.5 no-print">
                            {t.photos.map((p, i) => (
                              <a key={p.url + i} href={p.url} target="_blank" rel="noreferrer">
                                <img
                                  src={p.thumbnail_url || p.url}
                                  alt={p.filename || 'photo'}
                                  className="w-12 h-12 rounded object-cover border border-gray-200"
                                  loading="lazy"
                                />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {t.actual_duration ? (
                          <p className="text-xs font-medium text-gray-600">{formatDuration(t.actual_duration)}</p>
                        ) : null}
                        {t.completed_by && workers[t.completed_by] && (
                          <p className="text-[11px] text-gray-400">{workers[t.completed_by]}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
